import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { currentAccountConsumerSelector, isImpersonatingSelector } from 'store/account/selectors';
import { getNotificationsPreferences } from 'store/notifications/actions';
import {
  notificationsChannelsSelector,
  notificationsPreferencesFetchingSelector
} from 'store/notifications/selectors';
import { Channels } from 'store/notifications/types';

interface NotificationPreferences {
  doSmsEnroll?: boolean;
  hasInitialized?: boolean;
  isEmailEnabled?: boolean;
  isSmsEnabled?: boolean;
  notificationsChannels?: Channels;
}
let notificationPreferences: NotificationPreferences;
let hasFetched = false;

const useNotificationsOptOut = (showPromptFlag?: boolean): NotificationPreferences => {
  const consumer = useSelector(currentAccountConsumerSelector);
  const isImpersonating = useSelector(isImpersonatingSelector);
  const notificationsChannels = useSelector(notificationsChannelsSelector);
  const notificationsPreferencesLoading = useSelector(notificationsPreferencesFetchingSelector);
  const dispatch = useDispatch();

  const hasChannels = (notificationsChannels: Channels = {}) => {
    return !!Object.keys(notificationsChannels).length;
  };

  const preferencesLoaded = notificationPreferences !== undefined;

  useEffect(() => {
    if (
      showPromptFlag &&
      !isImpersonating &&
      !notificationsPreferencesLoading &&
      !hasChannels(notificationsChannels) &&
      !preferencesLoaded &&
      !hasFetched
    ) {
      dispatch(getNotificationsPreferences());
      hasFetched = true;
    }
  }, [showPromptFlag, isImpersonating, notificationsChannels, notificationsPreferencesLoading]);

  if (preferencesLoaded) return notificationPreferences;

  let doSmsEnroll;
  let hasInitialized;
  let isEmailEnabled;
  let isSmsEnabled;

  if (
    showPromptFlag &&
    !isImpersonating &&
    !notificationsPreferencesLoading &&
    hasChannels(notificationsChannels)
  ) {
    const { phones } = consumer;
    const hasValidPhone = !!phones?.[0]?.value;

    isEmailEnabled = !!Object.values(notificationsChannels).find(
      c => c.key.toLowerCase() === 'email' && c.enabled
    );
    isSmsEnabled = !!Object.values(notificationsChannels).find(
      c => c.key.toLowerCase() === 'sms' && c.enabled
    );
    doSmsEnroll = !isSmsEnabled && hasValidPhone;
    hasInitialized = true;
  }

  if (hasInitialized) {
    notificationPreferences = {
      doSmsEnroll,
      hasInitialized,
      isEmailEnabled,
      isSmsEnabled,
      notificationsChannels
    };
  }

  return notificationPreferences || ({} as NotificationPreferences);
};

export default useNotificationsOptOut;
